@if (!drawerMode) {
  <app-page-breadcrumb
    [pageTitle]="isEditing ? 'Editar Tarea' : 'Nueva Tarea'"
  ></app-page-breadcrumb>
}

<div [class.mt-4]="!drawerMode">
  <ng-container *ngIf="drawerMode; else cardWrap">
    <div class="p-6">
      <ng-container *ngTemplateOutlet="formTpl"></ng-container>
    </div>
  </ng-container>
  <ng-template #cardWrap>
    <app-component-card [title]="'Información de la Tarea'">
      <ng-container *ngTemplateOutlet="formTpl"></ng-container>
    </app-component-card>
  </ng-template>
</div>

<ng-template #formTpl>
  @if (loading && !form.value.title) {
    <div class="flex justify-center py-12">
      <div
        class="animate-spin rounded-full h-7 w-7 border-b-2 border-brand-500"
      ></div>
    </div>
  }

  @if (error) {
    <div
      class="mb-4 p-3 rounded-lg bg-error-50 border border-error-200 text-sm text-error-700 dark:bg-error-500/10 dark:border-error-500/20 dark:text-error-400"
    >
      {{ error }}
    </div>
  }

  <form
    [formGroup]="form"
    (ngSubmit)="guardar()"
    class="space-y-6"
  >
    <!-- ============ Identificación ============ -->
    <section class="space-y-4">
      <h3
        class="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 tracking-wider"
      >
        Identificación
      </h3>

      <div>
        <label
          class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Título <span class="text-error-500">*</span>
        </label>
        <input
          type="text"
          formControlName="title"
          placeholder="Ej. Llamar al cliente para confirmar reunión"
          class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
          [class.border-error-500]="hasError('title')"
          [class.border-gray-300]="!hasError('title')"
        />
        @if (hasError('title')) {
          <p class="mt-1.5 text-sm text-error-500">
            El título es obligatorio (máx. 500 caracteres).
          </p>
        }
      </div>

      <div>
        <label
          class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >Descripción</label
        >
        <textarea
          formControlName="description"
          rows="3"
          maxlength="5001"
          placeholder="Notas adicionales sobre la tarea (opcional)"
          class="dark:bg-dark-900 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm placeholder:text-gray-400 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
          [class.border-error-500]="hasError('description')"
          [class.border-gray-300]="!hasError('description')"
        ></textarea>
        @if (hasError('description')) {
          <p class="mt-1.5 text-sm text-error-500">
            La descripción no puede superar los 5000 carácteres
          </p>
        }
      </div>

      <!-- ======== Alerta preventiva: posibles tareas duplicadas (no bloquea el guardado) — Incidente 22 ======== -->
      @if (showDuplicateWarning) {
        <div
          class="rounded-lg border border-warning-200 bg-warning-50 p-4 dark:border-warning-500/30 dark:bg-warning-500/10"
        >
          <div class="flex items-start justify-between gap-3">
            <p
              class="text-sm font-semibold text-warning-700 dark:text-warning-400"
            >
              ⚠ Posibles tareas duplicadas
            </p>
            <button
              type="button"
              (click)="dismissDuplicateWarning()"
              title="Cerrar advertencia"
              class="shrink-0 p-1 rounded-lg text-warning-500 hover:text-warning-700 hover:bg-warning-100 dark:text-warning-400 dark:hover:text-warning-300 dark:hover:bg-warning-500/20 transition-colors"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>
          </div>
          <p class="mt-1 text-xs text-warning-600 dark:text-warning-400/80">
            Ya existen tareas con un título igual. Puede revisarlas antes de
            guardar — esta advertencia no impide continuar.
          </p>

          <div class="mt-3 space-y-2">
            @for (cand of visibleDuplicateCandidates; track cand.id) {
              <div
                class="flex flex-col gap-2 rounded-lg border border-warning-200/70 bg-white p-3 dark:border-warning-500/20 dark:bg-gray-900 sm:flex-row sm:items-start sm:justify-between"
              >
                <div class="min-w-0 flex-1">
                  <p
                    class="text-sm font-medium text-gray-900 dark:text-white truncate"
                  >
                    {{ cand.title }}
                  </p>
                  @if (cand.description) {
                    <p
                      class="text-xs text-gray-500 dark:text-gray-400 truncate"
                    >
                      {{ cand.description }}
                    </p>
                  }
                </div>
                <button
                  type="button"
                  (click)="verTareaDuplicada(cand.id)"
                  class="shrink-0 self-start text-xs font-medium text-brand-500 hover:text-brand-600 hover:underline dark:text-brand-400 dark:hover:text-brand-300"
                >
                  Ver tarea
                </button>
              </div>
            }
            @if (duplicateExtraCount > 0) {
              <p class="text-xs text-warning-600 dark:text-warning-400/80">
                y {{ duplicateExtraCount }} más…
              </p>
            }
          </div>
        </div>
      }
      <!-- ======== Fin alerta de duplicados ======== -->
    </section>

    <!-- ============ Tipo / Estado / Prioridad / Categoría como dropdowns ============ -->
    <section class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <!-- Tipo -->
      @if (types.length > 0) {
        <div
          class="relative"
          (click)="$event.stopPropagation()"
        >
          <label
            class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >Tipo de tarea</label
          >
          <button
            type="button"
            (click)="toggleDropdown('type', $event)"
            class="h-11 w-full flex items-center justify-between gap-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent dark:bg-gray-900 px-3 text-sm text-gray-800 dark:text-white/90 hover:border-gray-400 dark:hover:border-gray-600 transition-colors"
          >
            <span class="flex items-center gap-2 min-w-0">
              @if (selectedType(); as st) {
                <span
                  class="w-2.5 h-2.5 rounded-full shrink-0"
                  [style.background-color]="st.color || '#6B7280'"
                ></span>
                <span class="truncate">{{ st.label }}</span>
              } @else {
                <span class="text-gray-400">Seleccionar tipo...</span>
              }
            </span>
            <svg
              class="w-4 h-4 text-gray-400 shrink-0 transition-transform"
              [class.rotate-180]="openDropdown === 'type'"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          @if (openDropdown === 'type') {
            <ul
              class="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg"
            >
              @if (form.value.idTaskType !== null) {
                <li>
                  <button
                    type="button"
                    (click)="setType(null)"
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 italic"
                  >
                    Quitar selección
                  </button>
                </li>
              }
              @for (t of types; track t.id) {
                <li>
                  <button
                    type="button"
                    (click)="setType(t.id)"
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                    [class.bg-brand-50]="form.value.idTaskType === t.id"
                    [class.dark:bg-brand-900/30]="
                      form.value.idTaskType === t.id
                    "
                  >
                    <span
                      class="w-2.5 h-2.5 rounded-full shrink-0"
                      [style.background-color]="t.color || '#6B7280'"
                    ></span>
                    <span class="truncate">{{ t.label }}</span>
                  </button>
                </li>
              }
            </ul>
          }
        </div>
      }

      <!-- Estado -->
      @if (statuses.length > 0) {
        <div
          class="relative"
          (click)="$event.stopPropagation()"
        >
          <label
            class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Estado <span class="text-error-500">*</span>
          </label>
          <button
            type="button"
            (click)="toggleDropdown('status', $event)"
            class="h-11 w-full flex items-center justify-between gap-2 rounded-lg border bg-transparent dark:bg-gray-900 px-3 text-sm text-gray-800 dark:text-white/90 hover:border-gray-400 dark:hover:border-gray-600 transition-colors"
            [class.border-error-500]="hasError('idTaskStatus')"
            [class.border-gray-300]="!hasError('idTaskStatus')"
            [class.dark:border-gray-700]="!hasError('idTaskStatus')"
          >
            <span class="flex items-center gap-2 min-w-0">
              @if (selectedStatus(); as ss) {
                <span
                  class="w-2.5 h-2.5 rounded-full shrink-0"
                  [style.background-color]="ss.color || '#6B7280'"
                ></span>
                <span class="truncate">{{ ss.label }}</span>
                @if (ss.isFinal) {
                  <span
                    class="text-[10px] px-1 py-0.5 rounded bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400"
                    >final</span
                  >
                }
              } @else {
                <span class="text-gray-400">Seleccionar estado...</span>
              }
            </span>
            <svg
              class="w-4 h-4 text-gray-400 shrink-0 transition-transform"
              [class.rotate-180]="openDropdown === 'status'"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          @if (openDropdown === 'status') {
            <ul
              class="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg"
            >
              @for (s of statuses; track s.id) {
                <li>
                  <button
                    type="button"
                    (click)="setStatus(s.id)"
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                    [class.bg-brand-50]="form.value.idTaskStatus === s.id"
                    [class.dark:bg-brand-900/30]="
                      form.value.idTaskStatus === s.id
                    "
                  >
                    <span
                      class="w-2.5 h-2.5 rounded-full shrink-0"
                      [style.background-color]="s.color || '#6B7280'"
                    ></span>
                    <span class="truncate flex-1">{{ s.label }}</span>
                    @if (s.isFinal) {
                      <span
                        class="text-[10px] px-1 py-0.5 rounded bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400"
                        >final</span
                      >
                    }
                  </button>
                </li>
              }
            </ul>
          }
        </div>
      }

      <!-- Prioridad -->
      @if (priorities.length > 0) {
        <div
          class="relative"
          (click)="$event.stopPropagation()"
        >
          <label
            class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >Prioridad</label
          >
          <button
            type="button"
            (click)="toggleDropdown('priority', $event)"
            class="h-11 w-full flex items-center justify-between gap-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent dark:bg-gray-900 px-3 text-sm text-gray-800 dark:text-white/90 hover:border-gray-400 dark:hover:border-gray-600 transition-colors"
          >
            <span class="flex items-center gap-2 min-w-0">
              @if (selectedPriority(); as sp) {
                <span
                  class="w-2.5 h-2.5 rounded-full shrink-0"
                  [style.background-color]="sp.color || '#9CA3AF'"
                ></span>
                <span class="truncate">{{ sp.label }}</span>
              } @else {
                <span class="text-gray-400">Seleccionar prioridad...</span>
              }
            </span>
          </button>
          @if (openDropdown === 'priority') {
            <ul
              class="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg"
            >
              @if (form.value.idTaskPriority !== null) {
                <li>
                  <button
                    type="button"
                    (click)="setPriority(form.value.idTaskPriority)"
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 italic"
                  >
                    Quitar selección
                  </button>
                </li>
              }
              @for (p of priorities; track p.id) {
                <li>
                  <button
                    type="button"
                    (click)="setPriority(p.id)"
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                    [class.bg-brand-50]="form.value.idTaskPriority === p.id"
                    [class.dark:bg-brand-900/30]="
                      form.value.idTaskPriority === p.id
                    "
                  >
                    <span
                      class="w-2.5 h-2.5 rounded-full shrink-0"
                      [style.background-color]="p.color || '#9CA3AF'"
                    ></span>
                    <span class="truncate flex-1">{{ p.label }}</span>
                  </button>
                </li>
              }
            </ul>
          }
        </div>
      }

      <!-- Categoría -->
      @if (categories.length > 0) {
        <div
          class="relative"
          (click)="$event.stopPropagation()"
        >
          <label
            class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >Categoría</label
          >
          <button
            type="button"
            (click)="toggleDropdown('category', $event)"
            class="h-11 w-full flex items-center justify-between gap-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent dark:bg-gray-900 px-3 text-sm text-gray-800 dark:text-white/90 hover:border-gray-400 dark:hover:border-gray-600 transition-colors"
          >
            <span class="flex items-center gap-2 min-w-0">
              @if (selectedCategory(); as sc) {
                <span
                  class="w-2.5 h-2.5 rounded-full shrink-0"
                  [style.background-color]="sc.color || '#6B7280'"
                ></span>
                <span class="truncate">{{ sc.name }}</span>
              } @else {
                <span class="text-gray-400">Seleccionar categoría...</span>
              }
            </span>
            <svg
              class="w-4 h-4 text-gray-400 shrink-0 transition-transform"
              [class.rotate-180]="openDropdown === 'category'"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          @if (openDropdown === 'category') {
            <ul
              class="absolute z-30 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg"
            >
              @if (form.value.idTaskCategory !== null) {
                <li>
                  <button
                    type="button"
                    (click)="setCategory(form.value.idTaskCategory)"
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 italic"
                  >
                    Quitar selección
                  </button>
                </li>
              }
              @for (c of categories; track c.id) {
                <li>
                  <button
                    type="button"
                    (click)="setCategory(c.id)"
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                    [class.bg-brand-50]="form.value.idTaskCategory === c.id"
                    [class.dark:bg-brand-900/30]="
                      form.value.idTaskCategory === c.id
                    "
                  >
                    <span
                      class="w-2 h-2 rounded-full shrink-0"
                      [style.background-color]="c.color || '#6B7280'"
                    ></span>
                    <span class="truncate">{{ c.name }}</span>
                  </button>
                </li>
              }
            </ul>
          }
        </div>
      }
    </section>

    <!-- ============ Programación ============ -->
    <section class="space-y-3">
      <h3
        class="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 tracking-wider"
      >
        Programación
      </h3>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label
            class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >Fecha y hora de vencimiento</label
          >
          <input
            type="datetime-local"
            formControlName="datetimeDue"
            [min]="minDatetimeLocal"
            class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            [class.border-error-500]="hasError('datetimeDue')"
          />
          @if (hasError('datetimeDue')) {
            <p class="mt-1.5 text-sm text-error-500">
              La fecha no puede ser anterior a la actual.
            </p>
          }
        </div>

        <div>
          <label
            class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >Duración estimada</label
          >
          <div class="flex gap-2">
            <div class="relative flex-1">
              <input
                type="number"
                [(ngModel)]="durationDays"
                [ngModelOptions]="{ standalone: true }"
                (ngModelChange)="onDurationChange()"
                min="0"
                placeholder="0"
                class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent pl-3 pr-10 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <span
                class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none"
                >d</span
              >
            </div>
            <div class="relative flex-1">
              <input
                type="number"
                [(ngModel)]="durationHours"
                [ngModelOptions]="{ standalone: true }"
                (ngModelChange)="onDurationChange()"
                min="0"
                max="23"
                placeholder="0"
                class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent pl-3 pr-10 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <span
                class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none"
                >h</span
              >
            </div>
            <div class="relative flex-1">
              <input
                type="number"
                [(ngModel)]="durationMinutes"
                [ngModelOptions]="{ standalone: true }"
                (ngModelChange)="onDurationChange()"
                min="0"
                max="59"
                placeholder="30"
                class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent pl-3 pr-12 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <span
                class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none"
                >min</span
              >
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ Asignación ============ -->
    <section class="space-y-4">
      <h3
        class="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 tracking-wider"
      >
        Asignación
      </h3>

      <app-entity-picker
        mode="single"
        label="Responsable"
        placeholder="Buscar usuario por nombre o email... (Enter para ver todos)"
        helperText="El responsable de la tarea, quien la ejecutará. Por defecto, tú. Puedes reasignarla a otro usuario."
        [selectedId]="ownerSelected?.id ?? null"
        [selectedLabel]="ownerSelected?.label ?? null"
        [selectedSublabel]="ownerSelected?.sublabel ?? null"
        [searchFn]="searchAssignableUser"
        (selectionChange)="onOwnerChange($event)"
      />
    </section>

    <!-- ============ Asociaciones ============ -->
    <section class="space-y-4">
      <h3
        class="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 tracking-wider"
      >
        Asociaciones
      </h3>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <app-entity-picker
          mode="single"
          label="Empresa"
          placeholder="Buscar empresa... (Enter para ver todas)"
          [canCreateNew]="true"
          createNewLabel="Nueva"
          [selectedId]="companySelected?.id ?? null"
          [selectedLabel]="companySelected?.label ?? null"
          [selectedSublabel]="companySelected?.sublabel ?? null"
          [readonlySelection]="!!prefilledCompanyId"
          [autoFilledFlash]="companyAutoFillFlash"
          [searchFn]="searchCompany"
          (selectionChange)="onCompanyChange($event)"
          (createNewClicked)="abrirNuevaEmpresa()"
        />

        <app-entity-picker
          mode="single"
          label="Negocio (Deal)"
          placeholder="Buscar negocio... (Enter para ver todos)"
          [canCreateNew]="true"
          [selectedId]="dealSelected?.id ?? null"
          [selectedLabel]="dealSelected?.label ?? null"
          [selectedSublabel]="dealSelected?.sublabel ?? null"
          [readonlySelection]="!!prefilledDealId"
          [searchFn]="searchDeal"
          (selectionChange)="onDealChange($event)"
          (createNewClicked)="abrirNuevoDeal()"
        />

        <app-entity-picker
          mode="single"
          label="Lead"
          placeholder="Buscar lead... (Enter para ver todos)"
          [canCreateNew]="true"
          [selectedId]="leadSelected?.id ?? null"
          [selectedLabel]="leadSelected?.label ?? null"
          [selectedSublabel]="leadSelected?.sublabel ?? null"
          [readonlySelection]="!!prefilledLeadId"
          [searchFn]="searchLead"
          (selectionChange)="onLeadChange($event)"
          (createNewClicked)="abrirNuevoLead()"
        />

        <app-entity-picker
          mode="single"
          label="Tarea padre (subtarea)"
          placeholder="Buscar tarea... (Enter para ver todas)"
          [canCreateNew]="true"
          createNewLabel="Nueva"
          [selectedId]="parentTaskSelected?.id ?? null"
          [selectedLabel]="parentTaskSelected?.label ?? null"
          [selectedSublabel]="parentTaskSelected?.sublabel ?? null"
          [readonlySelection]="!!prefilledParentTaskId"
          [searchFn]="searchParentTask"
          (selectionChange)="onParentTaskChange($event)"
          (createNewClicked)="abrirNuevaTareaPadre()"
        />
      </div>

      <!-- Multi: contactos -->
      <div>
        <app-entity-picker
          mode="multi"
          label="Contactos asociados"
          placeholder="Buscar contacto por nombre o email... (Enter para ver todos)"
          helperText="Puedes asociar varios contactos a esta tarea. Si el contacto pertenece a una empresa, se sugerirá automáticamente."
          [canCreateNew]="true"
          [searchFn]="searchContact"
          (itemAdded)="onAddContact($event)"
          (createNewClicked)="abrirNuevoContacto()"
        />
        @if (selectedContacts.length > 0) {
          <div class="flex flex-wrap gap-2 mt-2">
            @for (c of selectedContacts; track c.id) {
              <span
                class="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-md bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 text-xs"
              >
                <span class="font-medium text-gray-800 dark:text-gray-100">{{
                  c.label
                }}</span>
                @if (c.sublabel) {
                  <span class="text-gray-500 dark:text-gray-400"
                    >· {{ c.sublabel }}</span
                  >
                }
                <button
                  type="button"
                  (click)="removeContact(c)"
                  class="p-0.5 rounded text-gray-400 hover:text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20"
                  title="Quitar"
                >
                  <svg
                    class="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </span>
            }
          </div>
        }
      </div>
    </section>

    <!-- ============ Seguidores ============ -->
    <section class="space-y-3">
      <div class="flex items-center justify-between gap-2">
        <h3
          class="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 tracking-wider"
        >
          Seguidores
        </h3>
        @if (isEditing && taskId) {
          <button
            type="button"
            (click)="toggleFollowSelf()"
            class="inline-flex items-center gap-1.5 px-3 py-1 text-xs rounded-md transition-colors"
            [class.bg-brand-500]="isFollowingSelf"
            [class.text-white]="isFollowingSelf"
            [class.bg-gray-100]="!isFollowingSelf"
            [class.text-gray-700]="!isFollowingSelf"
            [class.dark:bg-gray-800]="!isFollowingSelf"
            [class.dark:text-gray-300]="!isFollowingSelf"
          >
            @if (isFollowingSelf) {
              <svg
                class="w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                />
              </svg>
              Siguiendo
            } @else {
              + Seguir esta tarea
            }
          </button>
        }
      </div>

      <app-entity-picker
        mode="multi"
        placeholder="Buscar usuario... (Enter para ver todos)"
        helperText="Reciben notificaciones de cambios en la tarea."
        [searchFn]="searchUser"
        (itemAdded)="onAddFollower($event)"
      />
      @if (selectedFollowers.length > 0) {
        <div class="flex flex-wrap gap-2 mt-2">
          @for (f of selectedFollowers; track f.id) {
            <span
              class="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs"
            >
              <svg
                class="w-3 h-3 text-blue-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
              </svg>
              <span class="font-medium text-gray-800 dark:text-gray-100">{{
                f.label
              }}</span>
              <button
                type="button"
                (click)="removeFollower(f)"
                class="p-0.5 rounded text-gray-400 hover:text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20"
                title="Quitar"
              >
                <svg
                  class="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          }
        </div>
      }
    </section>

    <!-- ============ Dependencias (tareas bloqueantes) — INC 13 ============ -->
    <section class="space-y-3">
      <h3
        class="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 tracking-wider"
      >
        Dependencias
      </h3>
      <p class="text-xs text-gray-500 dark:text-gray-400">
        Esta tarea no podrá completarse hasta que las tareas bloqueantes estén
        finalizadas.
      </p>

      <div class="flex flex-col sm:flex-row sm:items-end gap-3">
        <div class="flex-1 min-w-0">
          <app-entity-picker
            mode="single"
            label="Bloqueada por"
            placeholder="Buscar tarea por título... (Enter para ver todas)"
            [selectedId]="newBlockedBy?.id ?? null"
            [selectedLabel]="newBlockedBy?.label ?? null"
            [selectedSublabel]="newBlockedBy?.sublabel ?? null"
            [searchFn]="searchBlocker"
            (selectionChange)="onBlockerChange($event)"
          />
        </div>
        <button
          type="button"
          (click)="agregarDependencia()"
          [disabled]="!newBlockedBy || addingDependency"
          class="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 h-11 text-xs font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
        >
          <svg
            class="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          Agregar
        </button>
      </div>

      <!-- EDIT: dependencias existentes -->
      @if (isEditing && dependencies.length > 0) {
        <div class="space-y-2">
          @for (d of dependencies; track d.id) {
            <div
              class="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700/50"
            >
              <div class="shrink-0">
                <svg
                  class="w-5 h-5"
                  [class.text-success-500]="d.blockedByIsFinal"
                  [class.text-warning-500]="!d.blockedByIsFinal"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  @if (d.blockedByIsFinal) {
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  } @else {
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  }
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <p
                  class="text-sm font-medium text-gray-900 dark:text-white truncate"
                >
                  <span [title]="'Identificador interno: ' + d.idTaskBlockedBy">{{ d.blockedByTaskTitle }}</span>
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Estado: {{ d.blockedByStatusName }}
                </p>
              </div>
              <span
                class="text-xs px-2 py-0.5 rounded-full font-medium"
                [class]="
                  d.blockedByIsFinal
                    ? 'bg-success-100 text-success-700 dark:bg-success-500/10 dark:text-success-400'
                    : 'bg-warning-100 text-warning-700 dark:bg-warning-500/10 dark:text-warning-400'
                "
              >
                {{ d.blockedByIsFinal ? 'Resuelta' : 'Pendiente' }}
              </span>
              <button
                type="button"
                (click)="quitarDependencia(d)"
                title="Quitar dependencia"
                class="shrink-0 p-1.5 rounded-md text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3"
                  />
                </svg>
              </button>
            </div>
          }
        </div>
      }

      <!-- CREATE: bloqueantes pendientes (en memoria, se crean al guardar) -->
      @if (!isEditing && pendingBlockerChips.length > 0) {
        <div class="flex flex-wrap gap-2">
          @for (c of pendingBlockerChips; track c.id) {
            <span
              class="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-md bg-warning-50 dark:bg-warning-500/10 border border-warning-200 dark:border-warning-500/20 text-xs"
            >
              <span class="font-medium text-gray-800 dark:text-gray-100">{{
                c.label
              }}</span>
              <button
                type="button"
                (click)="quitarDependenciaPendiente(c)"
                class="p-0.5 rounded text-gray-400 hover:text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20"
                title="Quitar"
              >
                <svg
                  class="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          }
        </div>
        <p class="text-xs text-gray-400 dark:text-gray-500">
          Las dependencias se crearán al guardar la tarea.
        </p>
      }
    </section>

    <!-- ============ Avanzado (collapsible) ============ -->
    <section class="border-t border-gray-200 dark:border-gray-700 pt-4">
      <button
        type="button"
        (click)="showAdvanced = !showAdvanced"
        class="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
      >
        <svg
          class="w-4 h-4 transition-transform"
          [class.rotate-90]="showAdvanced"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 5l7 7-7 7"
          />
        </svg>
        Opciones avanzadas
      </button>

      @if (showAdvanced) {
        <div class="mt-4 space-y-4">
          <label
            class="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/40 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
          >
            <div class="relative inline-flex shrink-0 mt-0.5">
              <input
                type="checkbox"
                formControlName="autoCompleteWhenSubtasksDone"
                class="peer sr-only"
              />
              <div
                class="w-11 h-6 bg-gray-300 dark:bg-gray-700 rounded-full peer-checked:bg-brand-500 transition-colors"
              ></div>
              <div
                class="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5"
              ></div>
            </div>
            <div class="flex-1">
              <span
                class="block text-sm font-medium text-gray-800 dark:text-gray-100"
                >Auto-completar al terminar subtareas</span
              >
              <span class="block text-xs text-gray-500 dark:text-gray-400"
                >Si se activa, esta tarea pasará automáticamente a estado final
                cuando todas sus subtareas estén completadas.</span
              >
            </div>
          </label>
        </div>
      }
    </section>

    <!-- Botones -->
    <div
      class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700"
    >
      <app-button
        variant="outline"
        type="button"
        (btnClick)="cancelar()"
        [disabled]="loading"
      >
        Cancelar
      </app-button>
      <app-button
        variant="primary"
        type="submit"
        [disabled]="form.invalid || loading"
      >
        {{
          loading
            ? 'Guardando…'
            : isEditing
              ? 'Actualizar Tarea'
              : 'Crear Tarea'
        }}
      </app-button>
    </div>
  </form>

  <!-- Modal: el contacto pertenece a múltiples empresas, deja al usuario elegir -->
  @if (showCompanyChoiceModal) {
    <div
      class="fixed inset-0 z-modal-top flex items-center justify-center bg-black/40"
      (click)="skipCompanyChoiceModal()"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-6 w-full max-w-md mx-4"
        (click)="$event.stopPropagation()"
      >
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          Elegir empresa
        </h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
          El contacto seleccionado pertenece a varias empresas. Selecciona la
          empresa que debe asociarse a esta tarea.
        </p>
        <ul class="space-y-2 max-h-72 overflow-y-auto">
          @for (a of contactCompaniesForModal; track a.id) {
            <li>
              <button
                type="button"
                (click)="selectCompanyFromModal(a)"
                class="w-full text-left px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <div
                  class="text-sm font-medium text-gray-800 dark:text-gray-100"
                >
                  {{ a.company?.commercialName || 'Empresa #' + a.idCompany }}
                  @if (a.isPrimary) {
                    <span
                      class="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300"
                      >Primaria</span
                    >
                  }
                </div>
                @if (a.position) {
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    {{ a.position }}
                  </div>
                }
              </button>
            </li>
          }
        </ul>
        <div class="mt-4 flex justify-end">
          <app-button
            variant="outline"
            type="button"
            (btnClick)="skipCompanyChoiceModal()"
            >Saltar</app-button
          >
        </div>
      </div>
    </div>
  }
</ng-template>
